import { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchMatches, FetchMatchesOptions } from '../api/fetchMatches';
import { Match } from '../types/matchTypes';

const useGetMatchesBase = (getOptions: (count: number, additionalOptions?: Partial<FetchMatchesOptions>) => FetchMatchesOptions) => {
    return (count: number, additionalOptions?: Partial<FetchMatchesOptions>) => {
        const [matches, setMatches] = useState<Match[]>([]);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState<Error | null>(null);

        const options = useMemo(() => getOptions(count, additionalOptions), [count, additionalOptions]);

        const fetchData = useCallback(async () => {
            setLoading(true);
            try {
                const result = await fetchMatches(options);
                setMatches(result.matches);
                setError(null);
            } catch (err) {
                setError(err instanceof Error ? err : new Error('An unknown error occurred'));
                setMatches([]);
            } finally {
                setLoading(false);
            }
        }, [options]);

        useEffect(() => {
            fetchData();
        }, [fetchData]);

        return { matches, loading, error };
    };
};

export const useGetMatches = (options: FetchMatchesOptions) => {
    const [matches, setMatches] = useState<Match[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const memoizedOptions = useMemo(() => options, [JSON.stringify(options)]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const result = await fetchMatches(memoizedOptions);
            setMatches(result.matches);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err : new Error('An unknown error occurred'));
            setMatches([]);
        } finally {
            setLoading(false);
        }
    }, [memoizedOptions]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { matches, loading, error };
};

export const useGetFutureMatches = useGetMatchesBase((count, additionalOptions = {}) => ({
    startDate: new Date(),
    limit: count,
    order: 'asc' as const,
    ...additionalOptions
}));

export const useGetPastMatches = useGetMatchesBase((count, additionalOptions = {}) => ({
    endDate: new Date(),
    limit: count,
    order: 'desc' as const,
    ...additionalOptions
}));